import { useState } from "react";
import { useParams } from "react-router-dom";
import { useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Compass, Check } from "lucide-react";
import { toast } from "sonner";

export default function PublicLandingPage() {
  const { slug } = useParams();
  const [landing, setLanding] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", destination_interest: "", notes: "" });

  useEffect(() => {
    api.get(`/landings/public/${slug}`).then((r) => setLanding(r.data)).catch(() => setLanding({ notFound: true }));
  }, [slug]);

  if (!landing) return <div className="min-h-screen flex items-center justify-center text-[#5C616A]">Cargando...</div>;

  if (landing.notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6]">
        <p className="text-[#5C616A]">Landing no encontrada.</p>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return toast.error("Nombre y email son obligatorios");
    try {
      await api.post(`/landings/public/${slug}/submit`, form);
      setSubmitted(true);
    } catch {
      toast.error("Error enviando formulario");
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center px-6 topo-bg">
        <div className="max-w-md text-center">
          <div className="w-14 h-14 rounded-full bg-[#2D6A4F] flex items-center justify-center mx-auto mb-6">
            <Check className="w-7 h-7 text-white" />
          </div>
          <h1 className="font-display text-4xl text-[#0A1128] mb-3">¡Gracias!</h1>
          <p className="text-[#5C616A]">Hemos recibido tu solicitud. Un asesor de viajes te contactará en menos de 24 horas con un itinerario personalizado.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <header className="px-6 lg:px-12 py-5 flex items-center gap-2 border-b border-[#E2DCD0]">
        <Compass className="w-4 h-4 text-[#D95D39]" strokeWidth={1.5} />
        <span className="font-display text-lg text-[#0A1128]">VoyageMarkt</span>
      </header>

      <div className="grid lg:grid-cols-2 min-h-[calc(100vh-65px)]">
        <div className="relative">
          {landing.hero_image ? (
            <img src={landing.hero_image} alt={landing.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-[#0A1128] to-[#1c2547]" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A1128]/60 to-transparent" />
          <div className="absolute bottom-10 left-10 right-10 text-white">
            <p className="text-[10px] uppercase tracking-[0.3em] mb-3 opacity-80">{landing.title}</p>
            <h1 className="font-display text-4xl xl:text-5xl leading-tight">{landing.headline}</h1>
          </div>
        </div>
        <div className="px-8 lg:px-16 py-12 flex flex-col justify-center">
          <p className="text-[#5C616A] leading-relaxed mb-8">{landing.subheadline}</p>

          {landing.bullets?.length > 0 && (
            <ul className="space-y-2 mb-8">
              {landing.bullets.map((b, i) => (
                <li key={i} className="flex gap-3 text-sm text-[#1A1F2B]">
                  <Check className="w-4 h-4 text-[#D95D39] shrink-0 mt-0.5" strokeWidth={2} />
                  {b}
                </li>
              ))}
            </ul>
          )}

          <form onSubmit={submit} className="space-y-3 max-w-md">
            <div><Label>Nombre*</Label><Input data-testid="public-form-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-sm bg-white border-[#E2DCD0]" /></div>
            <div><Label>Email*</Label><Input data-testid="public-form-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 rounded-sm bg-white border-[#E2DCD0]" /></div>
            <div><Label>Teléfono</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 rounded-sm bg-white border-[#E2DCD0]" /></div>
            <div><Label>Destino de interés</Label><Input value={form.destination_interest} onChange={(e) => setForm({ ...form, destination_interest: e.target.value })} className="mt-1 rounded-sm bg-white border-[#E2DCD0]" /></div>
            <div><Label>Cuéntanos más</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 rounded-sm bg-white border-[#E2DCD0]" /></div>
            <Button data-testid="public-form-submit" type="submit" className="w-full bg-[#D95D39] hover:bg-[#c54e2e] text-white rounded-sm h-11">{landing.cta_text}</Button>
          </form>
        </div>
      </div>
    </div>
  );
}
